import { useCallback } from 'react';

import { format } from 'date-fns';
import { groupBy, orderBy } from 'lodash-es';
import { useIntl } from 'react-intl';

interface IDate {
  date: string;
  [key: string]: any;
}

export const groupAndSortTransactionsByMonth = <T extends IDate>(tableData: T[]) => {
  const getTableGrouping = ({ date }: T) => format(new Date(date), 'yyyy-MM');

  const groupedTableData = Object.entries(groupBy(tableData, getTableGrouping));

  const groupedTableDataSortedByMonth = orderBy(groupedTableData, 0, 'desc');

  return groupedTableDataSortedByMonth;
};

export const useTransactionDateFormatters = () => {
  const { formatDate, formatDateToParts, formatTime } = useIntl();

  const getMonth = useCallback(
    <T extends IDate>(t: T) => {
      const parts = formatDateToParts(t.date, { month: 'long' });
      const month = parts.find(x => x.type === 'month');
      return month && month.value;
    },
    [formatDateToParts]
  );

  const formatTransactionDates = useCallback(
    <T extends IDate>(data: T[]): T[] => {
      const formatDateOptions = {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric'
      };
      return data.map(transaction => {
        const date = formatDate(transaction.date, formatDateOptions);
        const time = formatTime(transaction.date);
        return {
          ...transaction,
          date: `${date} ${time}`
        };
      });
    },
    [formatDate, formatTime]
  );

  return { getMonth, formatTransactionDates };
};
